import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Step,
  StepButton,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, DeleteOutline, FilterList, InfoOutlined, MoreVert, Search, FileDownload } from "@mui/icons-material";
import Autocomplete from "@mui/material/Autocomplete";
import MapPinLocation from "../../../shared/components/MapPinLocation.tsx";
import { getRap, getRapDetail } from "../services/rapService.tsx";
import { getProjectManagementReports } from "../services/projectManagementReportService.tsx";
import dayjs from "dayjs";

interface ProjectRow {
  id: string;
  projectName: string;
  source: string;
  kodeProject: string;
  region: string;
  projectValue: string;
  vendorName: string;
  projectPhase: string;
  startDate: string;
  endDate: string;
}

type SummaryRow = {
  category: string;
  bobotPercent: string;
  bobotMingguan: string;
  progressPrev: string;
  progressCurrent: string;
  progressCumulative: string;
};

type ReportFormState = {
  projectName: string;
  projectId: string;
  projectAddress: string;
  clientName: string;
  projectAO: string;
  salesRegion: string;
  latitude: string;
  longitude: string;
  city: string;
  district: string;
  startDate: string;
  endDate: string;
  evalInstalled: string;
  evalMaterialPlan: string;
  evalDetailKonsumtif: string;
  totalDuration: string;
  elapsedDuration: string;
  statusDuration: string;
  summaryRows: SummaryRow[];
  gmName: string;
  gmPosition: string;
  gmNotes: string;
  pmName: string;
  pmPosition: string;
  pmNotes: string;
};

type AhsItem = {
  id: string;
  name: string;
  category: string;
  description: string;
  volume: number;
  totalHargaUpahMaterial: number;
  jumlahHarga: number;
  week: string;
  weekNumber: number;
  startDate: string;
  endDate: string;
  totalDurasiActual: number;
  actualProgress: number;
};

type AhsFormState = {
  name: string;
  category: string;
  startDate: string;
  week: string;
  actualProgress: string;
};

type AhsOption = {
  id: string;
  name: string;
  category: string;
  description: string;
  volume: number;
  totalHargaUpahMaterial: number;
  jumlahHarga: number;
};

const summaryCategories = ["Pekerjaan Struktur", "Pekerjaan Arsitektur", "Pekerjaan MEP"];

const createInitialReportForm = (): ReportFormState => ({
  projectName: "",
  projectId: "",
  projectAddress: "",
  clientName: "",
  projectAO: "",
  salesRegion: "",
  latitude: "",
  longitude: "",
  city: "",
  district: "",
  startDate: "",
  endDate: "",
  evalInstalled: "",
  evalMaterialPlan: "",
  evalDetailKonsumtif: "",
  totalDuration: "",
  elapsedDuration: "",
  statusDuration: "",
  summaryRows: summaryCategories.map((category) => ({
    category,
    bobotPercent: "",
    bobotMingguan: "",
    progressPrev: "",
    progressCurrent: "",
    progressCumulative: "",
  })),
  gmName: "",
  gmPosition: "",
  gmNotes: "",
  pmName: "",
  pmPosition: "",
  pmNotes: "",
});

const createInitialAhsForm = (): AhsFormState => ({
  name: "",
  category: "",
  startDate: "",
  week: "",
  actualProgress: "",
});

const parseWeekNumber = (value: string) => {
  const match = /^W(\d+)$/i.exec(value.trim());
  return match ? Number(match[1]) : 0;
};

const parseNumber = (value?: string | number | null) => {
  if (value === undefined || value === null) return 0;
  if (typeof value === "number") return value;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? 0 : numeric;
};

const formatIdr = (value: number) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(value || 0);

const calculateAhsItem = (
  form: AhsFormState,
  projectStartDate: string,
  projectEndDate: string,
  option?: AhsOption
): AhsItem | null => {
  const startDate = form.startDate || projectStartDate;
  if (!startDate || !projectEndDate) return null;
  const start = dayjs(startDate);
  const end = dayjs(projectEndDate);
  if (!start.isValid() || !end.isValid()) return null;
  if (end.isBefore(start, "day")) return null;
  const totalHari = end.diff(start, "day") + 1;
  if (totalHari <= 0) return null;
  const actualProgress = Number(form.actualProgress || 0);
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    name: form.name,
    category: form.category || "Uncategorized",
    description: option?.description || form.name,
    volume: option?.volume ?? 0,
    totalHargaUpahMaterial: option?.totalHargaUpahMaterial ?? 0,
    jumlahHarga: option?.jumlahHarga ?? 0,
    week: form.week,
    weekNumber: parseWeekNumber(form.week),
    startDate,
    endDate: projectEndDate,
    totalDurasiActual: totalHari,
    actualProgress,
  };
};

const buildSCurveRows = (
  items: Array<
    AhsItem & {
      bobotRencana: number;
      bobotSatuan: number;
      targetBobotMingguan: number;
    }
  >
) => {
  const grouped = new Map<number, { target: number; actual: number }>();
  items.forEach((item) => {
    const key = item.weekNumber || parseWeekNumber(item.week);
    if (!key) return;
    const prev = grouped.get(key) || { target: 0, actual: 0 };
    grouped.set(key, {
      target: prev.target + item.targetBobotMingguan,
      actual: prev.actual + item.actualProgress,
    });
  });
  const weeks = Array.from(grouped.keys()).sort((a, b) => a - b);
  let cumulative = 0;
  return weeks.map((week) => {
    const values = grouped.get(week) || { target: 0, actual: 0 };
    cumulative += values.target;
    return {
      week,
      targetMingguan: values.target,
      actualMingguan: values.actual,
      kumulatif: cumulative,
    };
  });
};

const ProjectManagementReport: React.FC = () => {
  const [rows, setRows] = useState<ProjectRow[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [showSearch, setShowSearch] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [remoteTotalPages, setRemoteTotalPages] = useState<number | null>(null);
  const [openAdd, setOpenAdd] = useState(false);
  const [reportForm, setReportForm] = useState<ReportFormState>(() => createInitialReportForm());
  const [selectedRapProject, setSelectedRapProject] = useState<any | null>(null);
  const [rapOptions, setRapOptions] = useState<any[]>([]);
  const [rapLoading, setRapLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [ahsItems, setAhsItems] = useState<AhsItem[]>([]);
  const [ahsForm, setAhsForm] = useState<AhsFormState>(() => createInitialAhsForm());
  const [ahsFormOpen, setAhsFormOpen] = useState(false);
  const [ahsOptions, setAhsOptions] = useState<AhsOption[]>([]);
  const [ahsLoading, setAhsLoading] = useState(false);
  const ahsDerivedItems = useMemo(() => {
    const totalHarga = ahsItems.reduce((sum, item) => sum + item.jumlahHarga, 0);
    return ahsItems.map((item) => {
      const bobotRencana = totalHarga > 0 ? (item.jumlahHarga / totalHarga) * 100 : 0;
      const bobotSatuan = item.totalDurasiActual > 0 ? bobotRencana / item.totalDurasiActual : 0;
      const bobotHarian = item.totalDurasiActual > 0 ? bobotRencana / item.totalDurasiActual : 0;
      const targetBobotMingguan = bobotHarian * 7;
      const totalDurasiRencanaMinggu = Math.ceil(item.totalDurasiActual / 7);
      const totalDurasiActualMinggu = Math.ceil(item.totalDurasiActual / 7);
      return {
        ...item,
        bobotRencana,
        bobotSatuan,
        targetBobotMingguan,
        totalDurasiRencanaMinggu,
        totalDurasiActualMinggu,
      };
    });
  }, [ahsItems]);
  const scurveRows = useMemo(() => buildSCurveRows(ahsDerivedItems), [ahsDerivedItems]);
  const scurveByWeek = useMemo(() => {
    const map = new Map<number, { week: number; targetMingguan: number; actualMingguan: number; kumulatif: number }>();
    scurveRows.forEach((row) => {
      map.set(row.week, row);
    });
    return map;
  }, [scurveRows]);
  const selectedAhsOption = useMemo(
    () => ahsOptions.find((option) => option.name === ahsForm.name),
    [ahsOptions, ahsForm.name]
  );
  const totalHargaWithSelection = useMemo(() => {
    const base = ahsItems.reduce((sum, item) => sum + item.jumlahHarga, 0);
    return base + (selectedAhsOption?.jumlahHarga ?? 0);
  }, [ahsItems, selectedAhsOption]);
  const selectedBobotRencana = useMemo(() => {
    if (!selectedAhsOption) return 0;
    return totalHargaWithSelection > 0
      ? (selectedAhsOption.jumlahHarga / totalHargaWithSelection) * 100
      : 0;
  }, [selectedAhsOption, totalHargaWithSelection]);

  const steps = [
    "A. Data Proyek",
    "B. Waktu Pelaksanaan",
    "C. Rincian AHS",
    "D. Summary Mingguan",
  ];

  const filtered = useMemo(() => {
    if (!keyword.trim()) return rows;
    const term = keyword.toLowerCase();
    return rows.filter((row) =>
      [
        row.projectName,
        row.source,
        row.kodeProject,
        row.region,
        row.projectValue,
        row.vendorName,
        row.projectPhase,
      ]
        .join(" ")
        .toLowerCase()
        .includes(term)
    );
  }, [rows, keyword]);

  const totalPages = remoteTotalPages ?? Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const pagedRows = remoteTotalPages ? rows : filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const toggleSelect = (id: string) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const toggleSelectAll = (checked: boolean) => {
    setSelected(checked ? pagedRows.map((row) => row.id) : []);
  };

  const handleDeleteSelected = () => {
    if (!selected.length) return;
    setRows((prev) => prev.filter((row) => !selected.includes(row.id)));
    setSelected([]);
    setSuccessMessage("Selected reports deleted (mock)");
  };

  useEffect(() => {
    if (!openAdd) return;
    const fetchRapOptions = async () => {
      setRapLoading(true);
      try {
        const response = await getRap({ limit: 50, orderBy: "createdAt", order: "desc" });
        setRapOptions(response?.data || []);
      } catch (error) {
        console.error("Failed to fetch RAP data:", error);
        setRapOptions([]);
      } finally {
        setRapLoading(false);
      }
    };
    fetchRapOptions();
  }, [openAdd]);

  useEffect(() => {
    if (!openAdd || !selectedRapProject?.id) {
      setAhsOptions([]);
      return;
    }
    const fetchAhsOptions = async () => {
      setAhsLoading(true);
      try {
        const response = await getRapDetail({ limit: 1000000, rapId: selectedRapProject.id });
        const data = Array.isArray(response?.data) ? response.data : [];
        const mapped = data
          .map((item: any) => ({
            id: item.ahs?.id || item.ahsId || item.id,
            name: item.ahs?.namaAHS || "",
            category: item.ahs?.category || "",
            description: item.ahs?.namaAHS || "",
            volume: parseNumber(item.volume),
            totalHargaUpahMaterial: (parseNumber(item.hargaSatuanUpah) + parseNumber(item.hargaSatuanMaterial)) * parseNumber(item.volume),
            jumlahHarga: parseNumber(item.totalHarga),
          }))
          .filter((item: AhsOption) => item.name);
        setAhsOptions(mapped);
      } catch (error) {
        console.error("Failed to fetch AHS options:", error);
        setAhsOptions([]);
      } finally {
        setAhsLoading(false);
      }
    };
    fetchAhsOptions();
  }, [openAdd, selectedRapProject]);

  useEffect(() => {
    const fetchReports = async () => {
      setListLoading(true);
      try {
        const offset = (page - 1) * rowsPerPage;
        const response = await getProjectManagementReports({
          keyword: keyword.trim() || undefined,
          limit: rowsPerPage,
          offset,
          orderBy: "createdAt",
          order: "desc",
        });
        const data = Array.isArray(response?.data) ? response.data : [];
        const mapped = data.map((item: any, index: number) => ({
          id: item.id || item.projectMainId || `pm-${offset + index}`,
          projectName: item.projectName || item.project?.projectName || "",
          source: item.source || item.project?.source || "",
          kodeProject: item.projectReferenceId || item.projectId || item.project?.projectReferenceId || "",
          region: item.region || item.project?.region || "",
          projectValue: item.projectValue || item.nilaiProject || item.project?.valueEstimation || "",
          vendorName: item.vendorName || item.project?.vendorName || "",
          projectPhase: item.projectPhase || item.constructionPhase || item.project?.constructionPhase || "",
          startDate: item.startDate || item.project?.startDate || "",
          endDate: item.endDate || item.project?.endDate || "",
        }));
        setRows(mapped);
        if (response?.pagination?.totalPages) {
          setRemoteTotalPages(Number(response.pagination.totalPages));
        } else if (typeof response?.total === "number") {
          setRemoteTotalPages(Math.max(1, Math.ceil(response.total / rowsPerPage)));
        } else {
          setRemoteTotalPages(null);
        }
      } catch (error) {
        console.error("Failed to fetch project management reports:", error);
      } finally {
        setListLoading(false);
      }
    };
    fetchReports();
  }, [page, rowsPerPage, keyword]);


  const handleInputChange = (field: keyof ReportFormState) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setReportForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSummaryChange = (index: number, field: Exclude<keyof SummaryRow, "category">, value: string) => {
    setReportForm((prev) => {
      const next = [...prev.summaryRows];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, summaryRows: next };
    });
  };

  const handleMapSelect = ({ lat, lng }: { lat: number; lng: number }) => {
    setReportForm((prev) => ({ ...prev, latitude: lat.toString(), longitude: lng.toString() }));
  };

  const parseDateValue = (value?: string) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const totalDurationDays = useMemo(() => {
    const start = parseDateValue(reportForm.startDate);
    const end = parseDateValue(reportForm.endDate);
    if (!start || !end) return 0;
    const diffMs = end.getTime() - start.getTime();
    if (diffMs <= 0) return 0;
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  }, [reportForm.startDate, reportForm.endDate]);

  const elapsedDurationDays = useMemo(() => {
    const start = parseDateValue(reportForm.startDate);
    if (!start) return 0;
    const diffMs = new Date().getTime() - start.getTime();
    if (diffMs <= 0) return 0;
    return Math.min(totalDurationDays, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
  }, [reportForm.startDate, totalDurationDays]);

  const remainingDurationDays = Math.max(0, totalDurationDays - elapsedDurationDays);

  const ahsEndDate = reportForm.endDate;
  const ahsDurationDays = useMemo(() => {
    const start = parseDateValue(ahsForm.startDate || reportForm.startDate);
    const end = parseDateValue(ahsEndDate);
    if (!start || !end) return 0;
    const diffMs = end.getTime() - start.getTime();
    if (diffMs <= 0) return 0;
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  }, [ahsForm.startDate, ahsEndDate, reportForm.startDate]);
  const selectedBobotHarian = useMemo(() => {
    if (ahsDurationDays <= 0) return 0;
    return selectedBobotRencana / ahsDurationDays;
  }, [selectedBobotRencana, ahsDurationDays]);
  const selectedTargetMingguan = selectedBobotHarian * 7;

  const resetReportForm = () => {
    setReportForm(createInitialReportForm());
    setSelectedRapProject(null);
    setActiveStep(0);
    setAhsItems([]);
    setAhsForm(createInitialAhsForm());
    setAhsFormOpen(false);
  };

  const handleCloseAddModal = () => {
    setOpenAdd(false);
    resetReportForm();
  };

  const handleSelectRapProject = (_: any, newValue: any) => {
    setSelectedRapProject(newValue);
    if (!newValue) {
      resetReportForm();
      return;
    }
    const project = newValue.project || {};
    const projectName = project.projectName || newValue.projectName || "";
    const projectId = project.projectReferenceId || newValue.projectId || "";
    const projectAddress = project.projectAddress || newValue.projectAddress || "";
    const clientName = project.source || newValue.source || project.clientName || newValue.clientName || "";
    const projectAO = newValue.projectId || "";
    const region = project.region || newValue.region || "";
    const latitude = project.latitude ?? newValue.latitude;
    const longitude = project.longitude ?? newValue.longitude;
    const city = project.city || newValue.city || "";
    const district = project.district || newValue.district || "";
    const startDate = project.startDate || newValue.startDate;
    const endDate = project.endDate || newValue.endDate;

    const formatDate = (value?: string) => {
      if (!value) return "";
      return value.slice(0, 10);
    };

    setReportForm((prev) => ({
      ...prev,
      projectName,
      projectId,
      projectAddress,
      clientName,
      projectAO,
      salesRegion: region,
      latitude: latitude !== undefined && latitude !== null ? String(latitude) : prev.latitude,
      longitude: longitude !== undefined && longitude !== null ? String(longitude) : prev.longitude,
      city,
      district,
      startDate: formatDate(startDate) || prev.startDate,
      endDate: formatDate(endDate) || prev.endDate,
    }));
  };

  const isProjectSelected = Boolean(reportForm.projectName);

  const handleNextStep = () => {
    if (activeStep === steps.length - 1) return;
    setActiveStep((prev) => prev + 1);
  };

  const handleBackStep = () => {
    if (activeStep === 0) return;
    setActiveStep((prev) => prev - 1);
  };

  const handleStepClick = (stepIndex: number) => {
    if (stepIndex > 0 && !isProjectSelected) return;
    setActiveStep(stepIndex);
  };

  const handleAhsFormChange = (field: keyof AhsFormState) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setAhsForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSelectAhsName = (value: string) => {
    if (!value) {
      setAhsForm((prev) => ({ ...prev, name: "", category: "" }));
      return;
    }
    const selected = ahsOptions.find((option) => option.name === value);
    setAhsForm((prev) => ({
      ...prev,
      name: value,
      category: selected?.category || prev.category,
    }));
  };

  const handleSubmitAhs = () => {
    if (!ahsForm.name || !ahsForm.week) return;
    const option = ahsOptions.find((item) => item.name === ahsForm.name);
    const item = calculateAhsItem(ahsForm, reportForm.startDate, reportForm.endDate, option);
    if (!item) {
      setErrorMessage("Tanggal tidak valid. Pastikan End Date >= Start Date.");
      return;
    }
    const totalHarga = ahsItems.reduce((sum, current) => sum + current.jumlahHarga, 0) + item.jumlahHarga;
    const nextBobotRencana = totalHarga > 0 ? (item.jumlahHarga / totalHarga) * 100 : 0;
    const nextTargetBobotMingguan =
      item.totalDurasiActual > 0 ? (nextBobotRencana / item.totalDurasiActual) * 7 : 0;
    if (item.actualProgress > nextTargetBobotMingguan) {
      setErrorMessage("Actual Progress melebihi Target Bobot Mingguan.");
      return;
    }
    setErrorMessage(null);
    setAhsItems((prev) => [...prev, item]);
    setAhsForm(createInitialAhsForm());
    setAhsFormOpen(false);
  };

  const ahsSummaryRows = useMemo(() => {
    const grouped = new Map<string, { bobotPercent: number; bobotMingguan: number; progressThis: number }>();
    ahsDerivedItems.forEach((item) => {
      const key = item.category || "Uncategorized";
      const prev = grouped.get(key) || { bobotPercent: 0, bobotMingguan: 0, progressThis: 0 };
      grouped.set(key, {
        bobotPercent: prev.bobotPercent + item.bobotRencana,
        bobotMingguan: prev.bobotMingguan + item.targetBobotMingguan,
        progressThis: prev.progressThis + item.actualProgress,
      });
    });
    return Array.from(grouped.entries()).map(([category, values]) => ({
      category,
      bobotPercent: values.bobotPercent.toFixed(2),
      bobotMingguan: values.bobotMingguan.toFixed(3),
      progressPrev: "0.000",
      progressCurrent: values.progressThis.toFixed(3),
      progressCumulative: values.progressThis.toFixed(3),
    }));
  }, [ahsDerivedItems]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
        Project Management Report
      </Typography>
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.5,
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Typography>Show</Typography>
          <Select
            size="small"
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(1);
            }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>

          <Button
            variant="text"
            startIcon={<Search />}
            onClick={() => setShowSearch((prev) => !prev)}
            sx={{ color: "#333" }}
          >
            Search
          </Button>
          {showSearch && (
            <TextField
              size="small"
              placeholder="Type to search..."
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                setPage(1);
              }}
              sx={{ minWidth: 280 }}
            />
          )}

          <Button
            variant="text"
            color="error"
            disabled={!selected.length}
            startIcon={<DeleteOutline />}
            onClick={handleDeleteSelected}
          >
            Delete
          </Button>
          <Button variant="text" startIcon={<FilterList />} sx={{ color: "#333" }}>
            Filters
          </Button>
        </Box>

        <Button
          variant="contained"
          startIcon={<Add />}
          color="success"
          sx={{ borderRadius: 2 }}
          onClick={() => {
            resetReportForm();
            setOpenAdd(true);
          }}
        >
          Add New Report
        </Button>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
        Details: List Of All Product
      </Typography>

      <Paper elevation={0} sx={{ border: "1px solid #eee" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < pagedRows.length}
                    checked={pagedRows.length > 0 && selected.length === pagedRows.length}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                  />
                </TableCell>
                <TableCell>Project Name</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Kode Project</TableCell>
                <TableCell>Region</TableCell>
                <TableCell>Project Value</TableCell>
                <TableCell>Vendor Name</TableCell>
                <TableCell>Project Phase</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox checked={selected.includes(row.id)} onChange={() => toggleSelect(row.id)} />
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>{row.projectName}</Typography>
                  </TableCell>
                  <TableCell>{row.source}</TableCell>
                  <TableCell>{row.kodeProject}</TableCell>
                  <TableCell>{row.region}</TableCell>
                  <TableCell>{row.projectValue}</TableCell>
                  <TableCell>{row.vendorName}</TableCell>
                  <TableCell>{row.projectPhase}</TableCell>
                  <TableCell>{row.startDate}</TableCell>
                  <TableCell>{row.endDate}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="More">
                      <IconButton size="small">
                        <MoreVert />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}

              {listLoading && !pagedRows.length && (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4, color: "#777" }}>
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              )}
              {!listLoading && !pagedRows.length && (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4, color: "#777" }}>
                    No data
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Divider />
        <Box sx={{ p: 1.5, display: "flex", justifyContent: "flex-end" }}>
          <Pagination
            page={page}
            count={totalPages}
            onChange={(_, value) => setPage(value)}
            size="small"
          />
        </Box>
      </Paper>
      <Dialog open={openAdd} onClose={handleCloseAddModal} fullScreen>
        <DialogTitle>
          <Typography variant="h6" fontWeight={700}>
            Add New Report
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: "80vh" }}>
          <Stepper nonLinear activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label, index) => (
              <Step key={label} completed={index < activeStep}>
                <StepButton onClick={() => handleStepClick(index)} disabled={index > 0 && !isProjectSelected}>
                  {label}
                </StepButton>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 && (
            <>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                A. Data Proyek
              </Typography>
              <Box sx={{ p: 2, border: "1px solid #eee", borderRadius: 2, mb: 3, backgroundColor: "#fcfcfc" }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Autocomplete
                      fullWidth
                      options={rapOptions}
                      loading={rapLoading}
                      value={selectedRapProject}
                      onChange={handleSelectRapProject}
                      getOptionLabel={(option) => option?.project?.projectName || option?.projectName || ""}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          label="Project Name"
                          placeholder="Cari proyek dari RAP"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {rapLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Project Address"
                      value={reportForm.projectAddress}
                      InputProps={{ readOnly: true }}
                      helperText="Data diambil otomatis dari master proyek"
                      sx={{ "& .MuiInputBase-input": { backgroundColor: "#f4f5f7" } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Client Name"
                      value={reportForm.clientName}
                      InputProps={{ readOnly: true }}
                      helperText="Data diambil otomatis dari master proyek"
                      sx={{ "& .MuiInputBase-input": { backgroundColor: "#f4f5f7" } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box
                      sx={{
                        border: "1px solid #ddd",
                        borderRadius: 2,
                        height: 140,
                        width: "100%",
                        overflow: "hidden",
                      }}
                    >
                      <MapPinLocation
                        latitude={reportForm.latitude}
                        longitude={reportForm.longitude}
                        onSelect={handleMapSelect}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Input Latitude"
                      value={reportForm.latitude}
                      InputProps={{ readOnly: true }}
                      helperText="Data diambil otomatis dari master proyek"
                      sx={{ "& .MuiInputBase-input": { backgroundColor: "#f4f5f7" } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Input Longitude"
                      value={reportForm.longitude}
                      InputProps={{ readOnly: true }}
                      helperText="Data diambil otomatis dari master proyek"
                      sx={{ "& .MuiInputBase-input": { backgroundColor: "#f4f5f7" } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Select Region"
                      value={reportForm.salesRegion}
                      InputProps={{ readOnly: true }}
                      helperText="Data diambil otomatis dari master proyek"
                      sx={{ "& .MuiInputBase-input": { backgroundColor: "#f4f5f7" } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Input City"
                      value={reportForm.city}
                      InputProps={{ readOnly: true }}
                      helperText="Data diambil otomatis dari master proyek"
                      sx={{ "& .MuiInputBase-input": { backgroundColor: "#f4f5f7" } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Input District"
                      value={reportForm.district}
                      InputProps={{ readOnly: true }}
                      helperText="Data diambil otomatis dari master proyek"
                      sx={{ "& .MuiInputBase-input": { backgroundColor: "#f4f5f7" } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Project ID"
                      value={reportForm.projectId}
                      InputProps={{ readOnly: true }}
                      helperText="Data diambil otomatis dari master proyek"
                      sx={{ "& .MuiInputBase-input": { backgroundColor: "#f4f5f7" } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Start Date"
                      value={reportForm.startDate}
                      InputProps={{ readOnly: true }}
                      helperText="Data diambil otomatis dari master proyek"
                      sx={{ "& .MuiInputBase-input": { backgroundColor: "#f4f5f7" } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="End Date"
                      value={reportForm.endDate}
                      InputProps={{ readOnly: true }}
                      helperText="Data diambil otomatis dari master proyek"
                      sx={{ "& .MuiInputBase-input": { backgroundColor: "#f4f5f7" } }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </>
          )}

          {activeStep === 1 && (
            <>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                B. Waktu Pelaksanaan
              </Typography>
              <Box sx={{ p: 2, border: "1px solid #eee", borderRadius: 2, mb: 3, backgroundColor: "#fcfcfc" }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Jangka Waktu (hari)"
                      value={totalDurationDays}
                      InputProps={{ readOnly: true }}
                      helperText="Dihitung otomatis berdasarkan tanggal proyek"
                      sx={{ "& .MuiInputBase-input": { backgroundColor: "#f4f5f7" } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Waktu Yang Telah Berjalan"
                      value={elapsedDurationDays}
                      InputProps={{ readOnly: true }}
                      helperText="Dihitung otomatis berdasarkan tanggal proyek"
                      sx={{ "& .MuiInputBase-input": { backgroundColor: "#f4f5f7" } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Sisa Waktu Pelaksanaan"
                        value={remainingDurationDays}
                        InputProps={{ readOnly: true }}
                        helperText="Dihitung otomatis berdasarkan tanggal proyek"
                        sx={{ "& .MuiInputBase-input": { backgroundColor: "#f4f5f7" } }}
                      />
                      <Tooltip title="Dihitung otomatis berdasarkan tanggal proyek">
                        <InfoOutlined fontSize="small" color="action" />
                      </Tooltip>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}

          {activeStep === 2 && (
            <>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                C. Rincian AHS
              </Typography>
              <Box sx={{ p: 2, border: "1px solid #eee", borderRadius: 2, mb: 2, backgroundColor: "#fcfcfc" }}>
                <Button variant="outlined" startIcon={<Add />} onClick={() => setAhsFormOpen((prev) => !prev)}>
                  Tambah AHS
                </Button>
                {ahsFormOpen && (
                  <Box sx={{ mt: 2, p: 2, border: "1px dashed #c5c5c5", borderRadius: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="AHS Name"
                          value={ahsForm.name}
                          onChange={(e) => handleSelectAhsName(e.target.value)}
                        >
                          <MenuItem value="">Select AHS</MenuItem>
                          {ahsLoading && <MenuItem value="" disabled>Loading...</MenuItem>}
                          {ahsOptions.map((option) => (
                            <MenuItem key={option.id} value={option.name}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Category AHS"
                          value={ahsForm.category}
                          InputProps={{ readOnly: true }}
                          helperText="Data diambil otomatis dari master AHS"
                          sx={{ "& .MuiInputBase-input": { backgroundColor: "#f4f5f7" } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Select Week"
                          value={ahsForm.week}
                          onChange={handleAhsFormChange("week")}
                        >
                          <MenuItem value="">Select Week</MenuItem>
                          <MenuItem value="W1">W1</MenuItem>
                          <MenuItem value="W2">W2</MenuItem>
                          <MenuItem value="W3">W3</MenuItem>
                          <MenuItem value="W4">W4</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          type="date"
                          fullWidth
                          size="small"
                          label="Select Start Date"
                          InputLabelProps={{ shrink: true }}
                          value={ahsForm.startDate || reportForm.startDate}
                          onChange={handleAhsFormChange("startDate")}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="End Date (Auto Filled)"
                          value={ahsEndDate}
                          InputProps={{ readOnly: true }}
                          helperText="Data diambil otomatis dari master proyek"
                          sx={{ "& .MuiInputBase-input": { backgroundColor: "#f4f5f7" } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Total Durasi Actual"
                          value={ahsDurationDays}
                          InputProps={{ readOnly: true }}
                          helperText="Auto Generated"
                          sx={{ "& .MuiInputBase-input": { backgroundColor: "#f4f5f7" } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Bobot Harian"
                          value={selectedBobotHarian > 0 ? selectedBobotHarian.toFixed(2) : "0.00"}
                          InputProps={{ readOnly: true }}
                          helperText="Auto Generated"
                          sx={{ "& .MuiInputBase-input": { backgroundColor: "#f4f5f7" } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Target Bobot Mingguan"
                          value={selectedTargetMingguan > 0 ? selectedTargetMingguan.toFixed(2) : "0.00"}
                          InputProps={{ readOnly: true }}
                          helperText="Auto Generated"
                          sx={{ "& .MuiInputBase-input": { backgroundColor: "#f4f5f7" } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Actual Progress Minggu Ini"
                          value={ahsForm.actualProgress}
                          onChange={handleAhsFormChange("actualProgress")}
                          inputProps={{ inputMode: "decimal" }}
                        />
                        {Number(ahsForm.actualProgress || 0) > selectedTargetMingguan && (
                          <Typography variant="caption" color="warning.main">
                            Actual Progress melebihi Target Bobot Mingguan.
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
                      <Button variant="text" onClick={() => setAhsFormOpen(false)}>
                        Cancel
                      </Button>
                      <Button variant="contained" onClick={handleSubmitAhs} disabled={!ahsForm.name || !ahsForm.week}>
                        Submit AHS
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>

              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Tabel Item AHS
              </Typography>
              <Paper variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>No</TableCell>
                      <TableCell>Category AHS</TableCell>
                      <TableCell>Keterangan</TableCell>
                      <TableCell align="center">Volume</TableCell>
                      <TableCell align="right">Total Harga Upah + Material</TableCell>
                      <TableCell align="right">Jumlah Harga</TableCell>
                      <TableCell align="center">Bobot Rencana</TableCell>
                      <TableCell align="center">Bobot Satuan</TableCell>
                      <TableCell align="center">Total Durasi Rencana (Minggu)</TableCell>
                      <TableCell align="center">Total Durasi Actual (Minggu)</TableCell>
                      <TableCell align="center">Actual Start</TableCell>
                      <TableCell align="center">Actual End</TableCell>
                      <TableCell align="center">Target Bobot Minggu Ini</TableCell>
                      <TableCell align="center">Actual Progress Minggu Ini</TableCell>
                      <TableCell align="center">Progress Kumulatif Minggu Ini</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                      {ahsDerivedItems.map((item, index) => (
                        <TableRow key={item.id}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{item.category}</TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell align="center">{item.volume}</TableCell>
                        <TableCell align="right">{formatIdr(item.totalHargaUpahMaterial)}</TableCell>
                        <TableCell align="right">{formatIdr(item.jumlahHarga)}</TableCell>
                        <TableCell align="center">{item.bobotRencana.toFixed(2)}</TableCell>
                        <TableCell align="center">{item.bobotSatuan.toFixed(2)}</TableCell>
                        <TableCell align="center">{item.totalDurasiRencanaMinggu}</TableCell>
                        <TableCell align="center">{item.totalDurasiActualMinggu}</TableCell>
                        <TableCell align="center">{item.startDate}</TableCell>
                        <TableCell align="center">{item.endDate}</TableCell>
                        <TableCell align="center">{item.targetBobotMingguan.toFixed(2)}</TableCell>
                        <TableCell align="center">{item.actualProgress.toFixed(2)}</TableCell>
                        <TableCell align="center">
                          {(scurveByWeek.get(item.weekNumber)?.kumulatif ?? 0).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {ahsDerivedItems.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={15} align="center" sx={{ py: 2, color: "text.secondary" }}>
                          Belum ada AHS yang ditambahkan.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>

              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Tabel S-Curve RAP
              </Typography>
              <Paper variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>No</TableCell>
                      <TableCell>Category AHS</TableCell>
                      <TableCell>Keterangan</TableCell>
                      <TableCell align="center">Volume</TableCell>
                      <TableCell align="right">Total Harga Upah + Material</TableCell>
                      <TableCell align="center">Bobot Rencana</TableCell>
                      <TableCell align="center">Actual Progress Minggu Ini</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ahsDerivedItems.map((item, index) => (
                      <TableRow key={`scurve-${item.id}`}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{item.category}</TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell align="center">{item.volume}</TableCell>
                        <TableCell align="right">{formatIdr(item.totalHargaUpahMaterial)}</TableCell>
                        <TableCell align="center">{item.bobotRencana.toFixed(2)}</TableCell>
                        <TableCell align="center">{item.actualProgress.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                    {ahsDerivedItems.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 2, color: "text.secondary" }}>
                          Data S-Curve belum tersedia.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </>
          )}

          {activeStep === 3 && (
            <>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                D. Summary Mingguan
              </Typography>
              <Table size="small" sx={{ mb: 3 }}>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox />
                    </TableCell>
                    <TableCell>No</TableCell>
                    <TableCell>URAIAN PEKERJAAN (AHS Category)</TableCell>
                    <TableCell align="center">Bobot (%)</TableCell>
                    <TableCell align="center">Bobot Mingguan</TableCell>
                    <TableCell align="center">Progress S/D Minggu Lalu</TableCell>
                    <TableCell align="center">Progress Minggu Ini</TableCell>
                    <TableCell align="center">Progress S/D Minggu Ini</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ahsSummaryRows.map((row, index) => (
                    <TableRow key={row.category}>
                      <TableCell padding="checkbox">
                        <Checkbox />
                      </TableCell>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{row.category}</TableCell>
                      <TableCell align="center">{row.bobotPercent}</TableCell>
                      <TableCell align="center">{row.bobotMingguan}</TableCell>
                      <TableCell align="center">{row.progressPrev}</TableCell>
                      <TableCell align="center">{row.progressCurrent}</TableCell>
                      <TableCell align="center">{row.progressCumulative}</TableCell>
                    </TableRow>
                  ))}
                  {ahsSummaryRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 3, color: "text.secondary" }}>
                        Summary belum tersedia. Tambahkan AHS di Step 3.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <Box sx={{ p: 2, border: "1px solid #eee", borderRadius: 2, backgroundColor: "#fcfcfc" }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      required
                      label="Put Checker Name"
                      value={reportForm.gmName}
                      onChange={handleInputChange("gmName")}
                      helperText="Mandatory"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      required
                      label="Put Checker Position"
                      value={reportForm.gmPosition}
                      onChange={handleInputChange("gmPosition")}
                      helperText="Mandatory"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      required
                      label="Put Creator Name"
                      value={reportForm.pmName}
                      onChange={handleInputChange("pmName")}
                      helperText="Mandatory"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      required
                      label="Put Creator Position"
                      value={reportForm.pmPosition}
                      onChange={handleInputChange("pmPosition")}
                      helperText="Mandatory"
                    />
                  </Grid>
                </Grid>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "flex-end", gap: 1 }}>
          <Button variant="outlined" color="inherit" onClick={handleCloseAddModal}>
            Cancel
          </Button>
          <Button variant="outlined" onClick={handleBackStep} disabled={activeStep === 0}>
            Back
          </Button>
          {activeStep < steps.length - 1 ? (
            <Button variant="contained" color="primary" onClick={handleNextStep} disabled={!isProjectSelected}>
              Next
            </Button>
          ) : (
            <Button variant="contained" color="success">
              Save Report
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectManagementReport;
